"use strict";
/**
 * Asset Import Template Configuration
 *
 * Defines the XLSX template structure for each asset type's bulk import.
 * Single source of truth used by:
 *   - Backend: AssetImportTemplateService (XLSX generation)
 *   - Frontend: assetImportParser (file parsing + validation)
 *   - Backend: AssetSeedTemplateService (seed data generation)
 *
 * Template layout: "wide" — one sheet, one row per asset. Activity slots as column groups.
 * Used by Facility, ManufacturingFacility, DataCentre, Equipment, Vehicle.
 *
 * "Other" has no bulk import template (too open-ended).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ASSET_TEMPLATE_CONFIGS = exports.BULK_IMPORTABLE_ASSET_TYPES = void 0;
exports.isBulkImportableAssetType = isBulkImportableAssetType;
exports.getAssetTemplateConfig = getAssetTemplateConfig;
const enums_1 = require("./enums");
// ── Asset-specific intensity fields ──────────────────────────────────────────
const UNIT_COUNT_FIELD = {
    key: 'unitCount',
    label: 'Unit Count',
    description: 'Number of similar units this row represents (leave blank or 1 for a single unit)',
};
const FACILITY_FIELDS = [
    { key: 'floorAreaM2', label: 'Floor Area (m²)', description: 'Total floor area in square metres' },
    { key: 'employeeCount', label: 'Employee Count', description: 'Number of employees at this facility' },
    UNIT_COUNT_FIELD,
    { key: 'customIntensityLabel', label: 'Custom Intensity Label', description: 'Label for a custom intensity metric (e.g. "per room night")' },
    { key: 'customIntensityValue', label: 'Custom Intensity Value', description: 'Numeric value for the custom intensity metric' },
];
const MANUFACTURING_FIELDS = [
    { key: 'floorAreaM2', label: 'Floor Area (m²)', description: 'Total floor area in square metres' },
    { key: 'employeeCount', label: 'Employee Count', description: 'Number of employees at this facility' },
    UNIT_COUNT_FIELD,
    { key: 'customIntensityLabel', label: 'Custom Intensity Label', description: 'Label for a custom intensity metric' },
    { key: 'customIntensityValue', label: 'Custom Intensity Value', description: 'Numeric value for the custom intensity metric' },
];
const DATA_CENTRE_FIELDS = [
    { key: 'pue', label: 'PUE', description: 'Power Usage Effectiveness (1.0–5.0)' },
    { key: 'itLoadKw', label: 'IT Load (kW)', description: 'Total IT power load in kilowatts' },
    { key: 'rackCount', label: 'Rack Count', description: 'Number of server racks' },
    UNIT_COUNT_FIELD,
    { key: 'customIntensityLabel', label: 'Custom Intensity Label', description: 'Label for a custom intensity metric' },
    { key: 'customIntensityValue', label: 'Custom Intensity Value', description: 'Numeric value for the custom intensity metric' },
];
const EQUIPMENT_FIELDS = [
    { key: 'operatingHoursPerYear', label: 'Operating Hours / Year', description: 'Annual operating hours (max 8,760)' },
    { key: 'ratedCapacityKw', label: 'Rated Capacity (kW)', description: 'Equipment power rating in kilowatts' },
    UNIT_COUNT_FIELD,
    { key: 'customIntensityLabel', label: 'Custom Intensity Label', description: 'Label for a custom intensity metric' },
    { key: 'customIntensityValue', label: 'Custom Intensity Value', description: 'Numeric value for the custom intensity metric' },
];
const VEHICLE_FIELDS = [
    { key: 'vehicleType', label: 'Vehicle Type*', description: 'Type of vehicle (e.g. Car, Van, HGV)', required: true },
    { key: 'identifier', label: 'Identifier', description: 'Registration number, plate, or fleet ID (optional)' },
    UNIT_COUNT_FIELD,
    { key: 'customIntensityLabel', label: 'Custom Intensity Label', description: 'Label for a custom intensity metric' },
    { key: 'customIntensityValue', label: 'Custom Intensity Value', description: 'Numeric value for the custom intensity metric' },
];
// ── Activity slot definitions ────────────────────────────────────────────────
const SLOT_PURCHASED_ELECTRICITY = {
    key: 'purchased_electricity',
    label: 'Purchased Electricity',
    scope: enums_1.EmissionScope.Scope2,
    activityType: enums_1.Scope2ActivityType.PurchasedElectricity,
    fields: [],
};
const SLOT_PURCHASED_HEATING = {
    key: 'purchased_heating',
    label: 'Purchased Heating',
    scope: enums_1.EmissionScope.Scope2,
    activityType: enums_1.Scope2ActivityType.PurchasedHeating,
    fields: [],
};
const SLOT_PURCHASED_COOLING = {
    key: 'purchased_cooling',
    label: 'Purchased Cooling',
    scope: enums_1.EmissionScope.Scope2,
    activityType: enums_1.Scope2ActivityType.PurchasedCooling,
    fields: [],
};
const SLOT_PURCHASED_STEAM = {
    key: 'purchased_steam',
    label: 'Purchased Steam',
    scope: enums_1.EmissionScope.Scope2,
    activityType: enums_1.Scope2ActivityType.PurchasedSteam,
    fields: [],
};
const SLOT_HEATING_BOILERS = {
    key: 'heating_boilers',
    label: 'Heating & Boilers',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.StationaryCombustion,
    fields: ['fuelType'],
};
const SLOT_BACKUP_GENERATORS = {
    key: 'backup_generators',
    label: 'Backup Generators',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.StationaryCombustion,
    fields: ['fuelType'],
};
const SLOT_HVAC_REFRIGERATION = {
    key: 'hvac_refrigeration',
    label: 'HVAC & Refrigeration',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.FugitiveEmissions,
    fields: ['gasType'],
};
const SLOT_FURNACES_KILNS = {
    key: 'furnaces_kilns_boilers',
    label: 'Furnaces, Kilns & Boilers',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.StationaryCombustion,
    fields: ['fuelType'],
};
const SLOT_PROCESS_EMISSIONS = {
    key: 'process_emissions',
    label: 'Cement / Steel / Glass / Lime',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.ProcessEmissions,
    fields: ['processType'],
};
const SLOT_INDUSTRIAL_REFRIGERANTS = {
    key: 'industrial_refrigerants',
    label: 'Industrial Refrigerants & Gas',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.FugitiveEmissions,
    fields: ['gasType'],
};
const SLOT_IT_COOLING_ELECTRICITY = {
    key: 'it_cooling_electricity',
    label: 'IT & Cooling Electricity',
    scope: enums_1.EmissionScope.Scope2,
    activityType: enums_1.Scope2ActivityType.PurchasedElectricity,
    fields: [],
};
const SLOT_CHILLERS_CRAC = {
    key: 'chillers_crac',
    label: 'Chillers & CRAC Units',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.FugitiveEmissions,
    fields: ['gasType'],
};
const SLOT_MOBILE_FUEL = {
    key: 'mobile_fuel',
    label: 'Mobile Fuel',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.MobileCombustion,
    fields: ['fuelType'],
};
const SLOT_FUEL_CONSUMPTION = {
    key: 'fuel_consumption',
    label: 'Fuel Consumption',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.StationaryCombustion,
    fields: ['fuelType'],
};
const SLOT_GAS_LEAKAGE = {
    key: 'gas_leakage',
    label: 'Gas Leakage',
    scope: enums_1.EmissionScope.Scope1,
    activityType: enums_1.Scope1ActivityType.FugitiveEmissions,
    fields: ['gasType'],
};
// ── Template configs per asset type ──────────────────────────────────────────
const FACILITY_CONFIG = {
    templateType: 'wide',
    assetType: enums_1.AssetType.Facility,
    assetFields: FACILITY_FIELDS,
    activitySlots: [
        SLOT_PURCHASED_ELECTRICITY,
        SLOT_PURCHASED_HEATING,
        SLOT_PURCHASED_COOLING,
        SLOT_PURCHASED_STEAM,
        SLOT_HEATING_BOILERS,
        SLOT_BACKUP_GENERATORS,
        SLOT_HVAC_REFRIGERATION,
    ],
};
const MANUFACTURING_CONFIG = {
    templateType: 'wide',
    assetType: enums_1.AssetType.ManufacturingFacility,
    assetFields: MANUFACTURING_FIELDS,
    activitySlots: [
        SLOT_PURCHASED_ELECTRICITY,
        SLOT_PURCHASED_HEATING,
        SLOT_PURCHASED_COOLING,
        SLOT_PURCHASED_STEAM,
        SLOT_HEATING_BOILERS,
        SLOT_BACKUP_GENERATORS,
        SLOT_HVAC_REFRIGERATION,
        SLOT_FURNACES_KILNS,
        SLOT_PROCESS_EMISSIONS,
        SLOT_INDUSTRIAL_REFRIGERANTS,
    ],
};
const DATA_CENTRE_CONFIG = {
    templateType: 'wide',
    assetType: enums_1.AssetType.DataCentre,
    assetFields: DATA_CENTRE_FIELDS,
    activitySlots: [
        SLOT_IT_COOLING_ELECTRICITY,
        SLOT_BACKUP_GENERATORS,
        SLOT_CHILLERS_CRAC,
    ],
};
const EQUIPMENT_CONFIG = {
    templateType: 'wide',
    assetType: enums_1.AssetType.Equipment,
    assetFields: EQUIPMENT_FIELDS,
    activitySlots: [
        SLOT_FUEL_CONSUMPTION,
        SLOT_GAS_LEAKAGE,
    ],
};
const VEHICLE_CONFIG = {
    templateType: 'wide',
    assetType: enums_1.AssetType.Vehicle,
    assetFields: VEHICLE_FIELDS,
    activitySlots: [
        SLOT_MOBILE_FUEL,
    ],
};
// ── Public API ───────────────────────────────────────────────────────────────
/** Asset types that support bulk import templates (excludes Other) */
exports.BULK_IMPORTABLE_ASSET_TYPES = [
    enums_1.AssetType.Facility,
    enums_1.AssetType.ManufacturingFacility,
    enums_1.AssetType.DataCentre,
    enums_1.AssetType.Equipment,
    enums_1.AssetType.Vehicle,
];
/** Template config lookup — only for importable asset types */
exports.ASSET_TEMPLATE_CONFIGS = {
    [enums_1.AssetType.Facility]: FACILITY_CONFIG,
    [enums_1.AssetType.ManufacturingFacility]: MANUFACTURING_CONFIG,
    [enums_1.AssetType.DataCentre]: DATA_CENTRE_CONFIG,
    [enums_1.AssetType.Equipment]: EQUIPMENT_CONFIG,
    [enums_1.AssetType.Vehicle]: VEHICLE_CONFIG,
};
/** Type guard: is this asset type supported for bulk import? */
function isBulkImportableAssetType(assetType) {
    return exports.BULK_IMPORTABLE_ASSET_TYPES.includes(assetType);
}
/** Get the template config for an asset type. Returns undefined for non-importable types. */
function getAssetTemplateConfig(assetType) {
    if (!isBulkImportableAssetType(assetType))
        return undefined;
    return exports.ASSET_TEMPLATE_CONFIGS[assetType];
}
